import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { addBasePlate } from '../components/BasePlate';
import { addLighting } from '../components/Lighting';
import Walls from '../components/Walls';
import StraightStair from '../components/staircases/StraightStair';
import LStair from '../components/staircases/LStair';
import UStair from '../components/staircases/UStair';
import Controls from '../controls/Controls';
//import UIHandlers from '../controls/UIHandlers';
import PricingCalculator from '../helpers/PricingCalculator';
import InputListeners from '../ui/InputListeners';
import TabSwitching from '../ui/TabSwitching';
import { captureScreenshot } from '../helpers/ScreenshotUtils';
import { formatDataForPDF } from '../helpers/DataUtils';
import oakTextureURL from '../textures/oak_veneer_01_diff_4k_jpg.jpg';
import stairTreadModelURL from '../glTFModels/StairTreadUnparented.gltf';
import stairTreadBinURL from '../glTFModels/StairTreadUnparented.bin';
import SpiralStair from '../components/staircases/SpiralStair';
import BespokeSpiralStair from '../components/staircases/BespokeSpiralStair';
import { StairStateManager } from '../controls/StairStateManager';


class MainSceneSetup {
    constructor() {
        // Show loading spinner and hide canvas at start
        const loadingSpinner = document.getElementById('loading-spinner');
        const canvasContainer = document.getElementById('canvas-container');
        if (loadingSpinner) {
            loadingSpinner.classList.remove('hidden');
        }
        if (canvasContainer) {
            canvasContainer.style.visibility = 'hidden';
        }
    
        // Setup Three.js scene, camera, renderer
        this.scene = new THREE.Scene();
        this.camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
        
        // Set the initial camera position (adjusted to show the entire staircase)
        this.camera.position.set(10, 10, -15);
        
        this.renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.shadowMap.enabled = true;
    
        document.getElementById('canvas-container').appendChild(this.renderer.domElement);
        
        // Set the background color (based on the original project)
        this.scene.background = new THREE.Color(0xdae3e4);  // Soft light blue background
    
        // Initialize common components (lighting, baseplate, etc.)
        this.basePlate = addBasePlate(this.scene);
        console.log("Baseplate reference:", this.basePlate);
        addLighting(this.scene);
    
        // Initialize walls
       // this.walls = new Walls(this.scene, this.createWallMaterial(), null, null);
    
        // Initialize the ground plane
        const groundPlaneSize = 50;
        const groundGeometry = new THREE.PlaneGeometry(groundPlaneSize, groundPlaneSize);
        const groundMaterial = new THREE.MeshPhongMaterial({ color: 0xe6e5e1 });
        const groundPlane = new THREE.Mesh(groundGeometry, groundMaterial);
        groundPlane.rotation.x = -Math.PI / 2;
        groundPlane.position.y = -0.2;
        groundPlane.receiveShadow = true;
        this.scene.add(groundPlane);
        this.addLogoSprite(this.scene, groundPlaneSize);
    
        // Initialize camera controls
        this.controls = new Controls(this.camera, this.renderer);
    
        // Pricing system initialization
        this.pricingCalculator = new PricingCalculator();
    
        // Loader for GLTF models
        this.loader = new GLTFLoader();
        this.stairState = new StairStateManager(this);
        this.currentStaircase = null;
        console.log('currentStaircase:', this.currentStaircase);
        this.basicSpiralLanding = null;
        this.bespokeSpiralLanding = null;
        this.spiralLandingsLoaded = false;
    
        // UI Handlers
        this.uiHandlers = null;
    
        // Default stair type
        this.currentStairType = 'straight';
        this.bottomTreads = 0;
        this.topFlightTreads = 0;
        this.numberOfTreads = 0;
    
        // Initialize InputListeners and TabSwitching
        this.inputListeners = new InputListeners(this);
        if (!this.inputListeners.isInitialized) {
            this.inputListeners.init();
        }
    
        this.tabSwitching = new TabSwitching(this);
        this.tabSwitching.init();
    
        // Add resize event listener
        window.addEventListener('resize', this.handleResize.bind(this));
        window.addEventListener('orientationchange', this.handleResize.bind(this));
    
        // Initial resize to set correct dimensions
        this.handleResize();
       
        // Start the animation loop and show canvas when ready
        this.animate();
        Promise.all([
            new Promise(resolve => requestAnimationFrame(resolve))
        ]).then(() => {
            if (canvasContainer) {
                canvasContainer.style.visibility = 'visible';
            }
            if (loadingSpinner) {
                loadingSpinner.classList.add('hidden');
            }
        });
    }
   
    handleResize() {
        // Update camera aspect ratio
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();

        // Update renderer size
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(window.devicePixelRatio);

        // If using OrbitControls, update the controls
        if (this.controls) {
            this.controls.update();
        }

        // Force a re-render
        this.renderer.render(this.scene, this.camera);

        // Recenter the view on the staircase if it exists
        if (this.currentStaircase) {
            this.updateControlsTarget();
        }
    }
    // Helper function to create wall material
    createWallMaterial() {
        const textureLoader = new THREE.TextureLoader();
        const wallTexture = textureLoader.load(oakTextureURL);
        wallTexture.wrapS = THREE.RepeatWrapping;
        wallTexture.wrapT = THREE.RepeatWrapping;
        wallTexture.repeat.set(8, 8);

        return new THREE.MeshStandardMaterial({
            map: wallTexture,
            side: THREE.DoubleSide
        });
    }
    async enhanceWallsWithLandings() {
        console.log('Starting enhanceWallsWithLandings');
        if (!this.spiralLandingsLoaded) {
            const loader = new GLTFLoader();
            try {
                console.log('Loading landing models...');
                const [basicLandingGLTF, bespokeLandingGLTF] = await Promise.all([
                    loader.loadAsync('glTFModels/Spiral-Landing-dbl.gltf'),
                    loader.loadAsync('glTFModels/Bespoke-Spiral-Landing.gltf')
                ]);
    
                console.log('Landing models loaded');
    
                // Update walls with the new landings
                this.walls.basicSpiralLanding = basicLandingGLTF.scene;
                this.walls.bespokeSpiralLanding = bespokeLandingGLTF.scene;
    
                [this.walls.basicSpiralLanding, this.walls.bespokeSpiralLanding].forEach(landing => {
                    if (landing) {
                        landing.visible = false;
                        this.scene.add(landing);
                    }
                });
    
                this.spiralLandingsLoaded = true;
                console.log('Landings initialized:', {
                    basicLoaded: !!this.walls.basicSpiralLanding,
                    bespokeLoaded: !!this.walls.bespokeSpiralLanding
                });
            } catch (error) {
                console.error('Error loading spiral landings:', error, error.stack);
            }
        }
        return this.walls;
    }

    // Function to dynamically load and switch staircases
    async loadStaircase(type) {
        console.log('Loading staircase of type:', type);
        await this.stairState.handleTypeChange(type);
        // Get current values from UI before clearing the staircase
        const currentWidth = parseInt(document.getElementById('widthSlider').value, 10);
        const currentStraightTreads = parseInt(document.getElementById('stair-slider').value, 10);
        const currentBottomTreads = parseInt(document.getElementById('bottom-flight-slider').value, 10);
        const currentTopTreads = parseInt(document.getElementById('top-flight-slider').value, 10);
        // Determine if cantilever is selected
        const cantileverOption = document.querySelector('input[name="cantilever-option"]:checked').value;
        const isCantilever = cantileverOption === 'cantilever';
        console.log('Cantilever state detected:', isCantilever);
        
        // Clear the current staircase from the scene if it exists
        if (this.currentStaircase) {
            console.log('Cleaning up previous staircase...');
            
            // Clean up tread group
            if (this.currentStaircase.treadGroup) {
                this.currentStaircase.treadGroup.children.forEach(child => {
                    if (child.geometry) child.geometry.dispose();
                    if (child.material) {
                        if (Array.isArray(child.material)) {
                            child.material.forEach(mat => mat.dispose());
                        } else {
                            child.material.dispose();
                        }
                    }
                });
                this.scene.remove(this.currentStaircase.treadGroup);
            }

           // Enhanced cleanup of current staircase and components
        if (this.currentStaircase) {
            console.log('Cleaning up previous staircase...');
            
            // Clean up tread group
            if (this.currentStaircase.treadGroup) {
                this.currentStaircase.treadGroup.children.forEach(child => {
                    if (child.geometry) child.geometry.dispose();
                    if (child.material) {
                        if (Array.isArray(child.material)) {
                            child.material.forEach(mat => mat.dispose());
                        } else {
                            child.material.dispose();
                        }
                    }
                });
                this.scene.remove(this.currentStaircase.treadGroup);
            }

            // Specific cleanup for spiral stair landing
            if (this.currentStaircase.walls && this.currentStaircase.walls.spiralLanding) {
                this.scene.remove(this.currentStaircase.walls.spiralLanding);
                // Dispose of geometry and materials if necessary
                this.currentStaircase.walls.spiralLanding = null;
            }

            // Clear walls if they exist
            if (this.currentStaircase.walls) {
                if (this.currentStaircase.walls.backWall) {
                    this.scene.remove(this.currentStaircase.walls.backWall);
                    if (this.currentStaircase.walls.backWall.geometry) {
                        this.currentStaircase.walls.backWall.geometry.dispose();
                    }
                }
                if (this.currentStaircase.walls.sideWall) {
                    this.scene.remove(this.currentStaircase.walls.sideWall);
                    if (this.currentStaircase.walls.sideWall.geometry) {
                        this.currentStaircase.walls.sideWall.geometry.dispose();
                    }
                }
            }

            this.currentStaircase = null;
            console.log("Previous staircase and components removed.");
        }
    
            this.currentStaircase = null;
            console.log("Previous staircase removed.");
        }
    
        // Handle baseplate visibility
        if (this.basePlate) {
            this.basePlate.visible = !['Basic-Spiral', 'Bespoke-Spiral'].includes(type);
            console.log(`Baseplate visibility set to: ${this.basePlate.visible} for stair type: ${type}`);
        }
    
        // Determine the model path and stair type
        const modelPath = this.getModelPathForStaircase(type);
    
        // Load the new model
        this.loader.load(modelPath, async (gltf) => {
            const model = gltf.scene;
    
            let centralStringer, stub, treadPlate;
    
            // Traverse model and assign shadows
            model.traverse((child) => {
                if (child.isMesh) {
                    if (child.name.includes('Central') && child.name.includes('Stringer')) {
                        centralStringer = child;
                    } else if (child.name.includes('Stub')) {
                        stub = child;
                    } else if (child.name.includes('Tread') && child.name.includes('Plate')) {
                        treadPlate = child;
                    }
                    child.castShadow = true;
                    child.receiveShadow = true;
                }
            });
    
            // Create appropriate staircase instance
            switch (type) {
                case 'straight':
                    this.currentStaircase = new StraightStair(this.scene, model, this);
                    this.currentStaircase.updateTreadWidth(currentWidth);
                    this.currentStaircase.updateStairTreads(currentStraightTreads);
                    break;
                case 'L-shaped':
                    this.currentStaircase = new LStair(this.scene, model, this);
                    this.currentStaircase.updateTreadWidth(currentWidth);
                    this.currentStaircase.updateStairTreads(currentBottomTreads, currentTopTreads);
                    break;
                case 'U-shaped':
                    this.currentStaircase = new UStair(this.scene, model, this);
                    this.currentStaircase.updateTreadWidth(currentWidth);
                    this.currentStaircase.updateStairTreads(currentBottomTreads, currentTopTreads);
                    break;
                    case 'Basic-Spiral':
                        case 'Bespoke-Spiral':
                            console.log('Loading spiral staircase type:', type);
                            const staircaseClass = type === 'Basic-Spiral' ? SpiralStair : BespokeSpiralStair;
                            this.currentStaircase = new staircaseClass(this.scene, model, this);
                            await this.currentStaircase.ready; // Wait for landing to load
                            await this.currentStaircase.updateStairTreads(currentStraightTreads);
                            break;
                default:
                    console.error('Unknown staircase type:', type);
                    return;
            }
           
            // Set the current stair type
            this.currentStairType = type;
            if (this.inputListeners && typeof this.inputListeners.updateMirrorButtonVisibility === 'function') {
                this.inputListeners.updateMirrorButtonVisibility();
            }
            // Initialize staircase
            if (typeof this.currentStaircase.initialize === 'function') {
                await this.currentStaircase.initialize();
            }
    
            // Assign component references
            this.currentStaircase.centralStringer = centralStringer;
            this.currentStaircase.stub = stub;
            this.currentStaircase.treadPlate = treadPlate;
    
            await this.updateControlsTarget();
            
            // Update pricing calculator
            await this.pricingCalculator.setStairType(type, this.currentStaircase, isCantilever);
            
            // Calculate price
            this.calculatePrice();
    
            // Apply cantilever toggle if not a spiral stair
            if (!type.includes('Spiral')) {
                this.applyCantileverToggle();
            }
        });
    }

    applyCantileverToggle() {
        const isCantilever = document.querySelector('input[name="cantilever-option"]:checked').value === 'cantilever';
    
        if (this.currentStaircase) {
            console.log("Current staircase is ready.", this.currentStaircase);
            const { centralStringer, stub, treadPlate } = this.currentStaircase;
            console.log("Components:", centralStringer, stub, treadPlate);
    
            // Toggle visibility using material transparency
            if (centralStringer && centralStringer.material) {
                centralStringer.material.transparent = true;
                centralStringer.material.opacity = isCantilever ? 0 : 1;
                centralStringer.material.needsUpdate = true;
                console.log("Central Stringer visibility:", centralStringer.material.opacity);
            }
            if (stub && stub.material) {
                stub.material.transparent = true;
                stub.material.opacity = isCantilever ? 0 : 1;
                stub.material.needsUpdate = true;
                console.log("Stub visibility:", stub.material.opacity);
            }
            if (treadPlate && treadPlate.material) {
                treadPlate.material.transparent = true;
                treadPlate.material.opacity = isCantilever ? 0 : 1;
                treadPlate.material.needsUpdate = true;
                console.log("Tread Plate visibility:", treadPlate.material.opacity);
            }
    
            // Update walls if the method exists
            if (typeof this.currentStaircase.updateWallsWithBoundingBox === 'function') {
                this.currentStaircase.updateWallsWithBoundingBox();
            }
        }
    
        // Toggle baseplate visibility
        if (this.basePlate) {
            this.basePlate.visible = !isCantilever;
            console.log("Baseplate visibility:", this.basePlate.visible);
        }
    
        // Force re-render
        this.renderer.render(this.scene, this.camera);
    }
    
    
    
    // Helper function to update the camera's target and fit staircase into view
    updateControlsTarget() {
        if (!this.currentStaircase || !this.currentStaircase.treadGroup) {
            console.error("Error: currentStaircase or treadGroup is undefined.");
            return;
        }

        const boundingBox = new THREE.Box3().setFromObject(this.currentStaircase.treadGroup);
        const center = boundingBox.getCenter(new THREE.Vector3());
        const size = boundingBox.getSize(new THREE.Vector3());

        // Set the camera focus to the center of the staircase
        this.controls.setTarget(center);

        // Calculate the required distance based on the staircase size and tread count
        const maxDimension = Math.max(size.x, size.y, size.z);
        const fov = this.camera.fov * (Math.PI / 180);
        let cameraZ = Math.abs(maxDimension / 2 / Math.tan(fov / 2));
        
        // Adjust zoom based on tread count
        const numTreads = this.getStaircaseTotalTreads();
        const zoomFactor = 1 + (numTreads / 15); // Adjust this factor as needed
        cameraZ *= zoomFactor;

        // Calculate camera position
        const theta = Math.PI / 1.5; 
        const phi = -Math.PI / 6; // -30 degrees
        const cameraX = cameraZ * Math.sin(theta) * Math.cos(phi);
        const cameraY = cameraZ * Math.sin(phi);
        const cameraZPos = cameraZ * Math.cos(theta) * Math.cos(phi);

        const cameraPosition = new THREE.Vector3(
            center.x + cameraX,
            center.y + cameraY,
            center.z + cameraZPos
        );
        
        this.camera.position.copy(cameraPosition);
        this.camera.lookAt(center);
        
        // Update the camera's near and far planes
        this.camera.near = cameraZ / 100;
        this.camera.far = cameraZ * 100;
        this.camera.updateProjectionMatrix();

        // Force an update of the controls
        this.controls.update();
    }

    getStaircaseTotalTreads() {
        if (this.currentStaircase) {
            const numTreads = this.currentStaircase.numTreads || this.currentStaircase.numTreads || 0;
            console.log('getStaircaseTotalTreads:', numTreads);
            return numTreads;
        }
        return 0;
    }

    // Update this method to call updateControlsTarget
    updateStairTreads(numTreads) {
        if (this.currentStaircase) {
            this.currentStaircase.updateStairTreads(numTreads);
            this.updateControlsTarget(); // Update camera after changing treads
        }
    }
    updateMaterial(texturePath) {
        if (!this.currentStaircase || !this.currentStaircase.treadGroup) {
            console.warn("No staircase or tread group available to update material.");
            return;
        }

        this.currentStaircase.treadGroup.children.forEach(tread => {
            tread.traverse((child) => {
                if (child.isMesh) {
                    // Toggle visibility based on material selection
                    if (child.name === 'Tread') {
                        child.visible = (texturePath !== "none"); // Hide if 'No Tread' is selected
                    } else {
                        child.visible = true; // Ensure other parts remain visible
                    }

                    // Apply texture if a valid path is provided
                    if (texturePath !== "none" && child.name === 'Tread') {
                        const textureLoader = new THREE.TextureLoader();
                        const materialTexture = textureLoader.load(texturePath);
                        materialTexture.wrapS = THREE.RepeatWrapping;
                        materialTexture.wrapT = THREE.RepeatWrapping;
                        child.material.map = materialTexture;
                        child.material.needsUpdate = true;
                    }
                }
            });
        });
    }

    animate() {
        const animateCallback = () => {
            requestAnimationFrame(animateCallback);
            this.controls.update(); // Update camera controls
            this.renderer.render(this.scene, this.camera);
        };
        animateCallback();
    }
  
    // Helper function to get the GLTF model path based on the staircase type
    getModelPathForStaircase(type) {
        switch (type) {
            case 'straight':
            case 'U-shaped':
            case 'L-shaped':
                return 'glTFModels/StairTreadUnparented.gltf';
            case 'Basic-Spiral':
                return 'glTFModels/SpiralKitsetTallPole.gltf';
            case 'Bespoke-Spiral':
                return 'glTFModels/Spiral-bespoke.gltf';
            default:
                console.error('Invalid staircase type for model loading:', type);
                return '';
        }
    }

    // Calculate total price based on current options
    async calculatePrice() {
        const options = {
            numTreads: this.getStaircaseTotalTreads(),
            width: parseInt(document.getElementById('widthSlider').value, 10),
            material: document.querySelector('input[name="materialRadio"]:checked').value
        };
    
        // Wait for the pricing module to be ready
        if (!this.pricingCalculator.pricingModule) {
            console.warn('Pricing module not ready yet.');
            return;
        }
        console.log('calculatePrice called with options:', options);
        console.log('Current stairType:', this.pricingCalculator.stairType);
        console.log('Cantilever state:', document.querySelector('input[name="cantilever-option"]:checked').value);

        const totalPrice = this.pricingCalculator.calculateTotal(options);
        this.calculatedPrice = totalPrice;
    
        // Store options in the pricing calculator
        this.pricingCalculator.numTreads = options.numTreads;
        this.pricingCalculator.width = options.width;
        this.pricingCalculator.selectedMaterial = options.material;
    
        // Update the UI with the calculated price
        const priceElement = document.getElementById('price');
        if (priceElement) {
            priceElement.textContent = `$${totalPrice.toFixed(2)}`;
        } else {
            console.error('Price element not found!');
        }
    }
    

    // Start rendering the scene
    animate() {
        const animateCallback = () => {
            requestAnimationFrame(animateCallback);
            this.controls.update(); // Update camera controls
            this.renderer.render(this.scene, this.camera); // Render the scene
        };
        animateCallback();
    }
    setTreadCount(numTreads) {
        this.numberOfTreads = numTreads; // Store the total number of treads
    }
    setFlightTreads(bottomTreads, topFlightTreads) {
        this.bottomTreads = bottomTreads;
        this.topFlightTreads = topFlightTreads;
        this.numberOfTreads = bottomTreads + topFlightTreads;
        console.log('MainSceneSetup - setFlightTreads called with bottomTreads:', bottomTreads, 'topFlightTreads:', topFlightTreads);
    }
    getUIState() {
        return {
            width: parseInt(document.getElementById('widthSlider').value, 10),
            bottomTreads: parseInt(document.getElementById('bottom-flight-slider').value, 10),
            topTreads: parseInt(document.getElementById('top-flight-slider').value, 10),
            straightTreads: parseInt(document.getElementById('stair-slider').value, 10),
            material: document.querySelector('input[name="materialRadio"]:checked').value,
            cantileverOption: document.querySelector('input[name="cantilever-option"]:checked').value
        };
    }
    
    getCalculatorData() {
        const uiState = this.getUIState();
        const calculatorData = {
            staircaseType: this.currentStairType,
            numberOfTreads: this.currentStaircase?.numTreads || 14,
            width: uiState.width,
            material: uiState.material,
            cantilever: uiState.cantileverOption === 'cantilever'
        };
    
        // Add flight-specific data for L-shaped and U-shaped stairs
        if (this.currentStairType === 'L-shaped' || this.currentStairType === 'U-shaped') {
            calculatorData.bottomTreads = this.bottomTreads;
            calculatorData.topFlightTreads = this.topFlightTreads;
            calculatorData.numberOfTreads = this.bottomTreads + this.topFlightTreads;
        }
    
        console.log('Getting calculator data:', {
            currentType: this.currentStairType,
            calculatorData
        });
    
        return calculatorData;
    }
    
    // Function to update controls, interact with UI, and recalculate price on input change
    setupControls() {
        document.getElementById('stair-slider').addEventListener('input', (event) => {
            const numTreads = parseInt(event.target.value, 10);
            if (this.currentStaircase) {
                this.currentStaircase.updateStairTreads(numTreads);
            }
            this.updateControlsTarget(); // Update controls when the staircase changes
            this.calculatePrice();  // Recalculate the price whenever the user changes treads
        });

        document.getElementById('widthSlider').addEventListener('input', (event) => {
            const width = parseInt(event.target.value, 10);
            if (this.currentStaircase) {
                this.currentStaircase.updateTreadWidth(width);
            }
            this.updateControlsTarget(); // Update controls when the width changes
            this.calculatePrice();  // Recalculate the price when the width is changed
        });

        document.querySelectorAll('input[name="materialRadio"]').forEach((radio) => {
            radio.addEventListener('change', () => {
                const selectedMaterial = document.querySelector('input[name="materialRadio"]:checked').value;
                this.currentStaircase.updateMaterial(selectedMaterial);
                this.calculatePrice();  // Recalculate the price when the material is changed
            });
        });    
    }
    addLogoSprite(scene, groundPlaneSize) {
        const textureLoader = new THREE.TextureLoader();
    textureLoader.load('../textures/logo.png', (texture) => {
        const material = new THREE.MeshBasicMaterial({
            map: texture,
            transparent: true,
            opacity: 1,
            side: THREE.DoubleSide // Ensures both sides of the plane are visible
        });

        // Aspect ratio calculation for proper scaling
        const aspectRatio = texture.image.width / texture.image.height;
        const desiredHeight = 2; // Adjust for desired size
        const desiredWidth = desiredHeight * aspectRatio;

        const positions = [
            { x: 0, y: 5, z: groundPlaneSize / 2 },       // Centered on the front edge
            { x: groundPlaneSize / 2, y: 5, z: 0 },       // Centered on the right edge
            { x: 0, y: 5, z: -groundPlaneSize / 2 },      // Centered on the back edge
            { x: -groundPlaneSize / 2, y: 5, z: 0 }       // Centered on the left edge
        ];

        const rotations = [
            Math.PI,                     // Face inward from the front edge
            -Math.PI / 2,                // Face inward from the right edge
            0,                           // Face inward from the back edge
            Math.PI / 2                  // Face inward from the left edge
        ];

        positions.forEach((pos, index) => {
            const logoPlane = new THREE.Mesh(new THREE.PlaneGeometry(desiredWidth, desiredHeight), material);
            logoPlane.position.set(pos.x, pos.y, pos.z);
            logoPlane.rotation.y = rotations[index];
            scene.add(logoPlane);
        });
        });
    }
    //capture Screenshot for form submission
    captureScreenshot() {
        try {
             // Adjust the camera position and target before capturing the screenshot
            // Adjust the camera position and target based on the staircase type
            switch (this.currentStairType) {
                case 'straight':
                    this.camera.position.set(15, 15, -20); // Example camera position for straight staircase
                    break;
                case 'L-shaped':
                    this.camera.position.set(0, 15, -20); // Adjust the position for L-shaped
                    break;
                case 'U-shaped':
                    this.camera.position.set(25, 15, 0); // Adjust the position for U-shaped
                    break;
                case 'Spiral':
                case 'Bespoke-Spiral':
                this.camera.position.set(15, 15, -20); // Adjust the position for Spiral
                break;
                default:
                    this.camera.position.set(10, 10, 0); // Default position
                    break;
            }
            this.camera.lookAt(new THREE.Vector3(0, 0, 0)); // Make the camera look at the origin (center of the scene)
            this.controls.update();
            // You may also want to adjust other camera properties like field of view (FOV)
            this.camera.fov = 40; // Change the FOV if necessary (default is usually 50)
            this.camera.updateProjectionMatrix(); // After changing FOV or other properties, update the matrix

            // Render the scene before capturing
            this.renderer.render(this.scene, this.camera);
            // Create a screenshot from the current renderer's canvas
            const dataUrl = this.renderer.domElement.toDataURL('image/png');
            const byteString = atob(dataUrl.split(',')[1]);
            const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];
            const arrayBuffer = new ArrayBuffer(byteString.length);
            const uint8Array = new Uint8Array(arrayBuffer);
            
            for (let i = 0; i < byteString.length; i++) {
                uint8Array[i] = byteString.charCodeAt(i);
            }
    
            const blob = new Blob([arrayBuffer], { type: mimeString });
            return blob;  // Return the screenshot as a Blob
        } catch (error) {
            console.error('Error capturing screenshot:', error);
            return null;
        }
    }
}

export default MainSceneSetup;